import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useEffectOnce, useList, useUnmount } from 'react-use'
import { useRouter } from 'next/router'
import { stems } from 'player/src/utils/utils'
import { off, on } from '../../../lib/events'
import { TaskOperations } from '../../../types'
import { getMixerInitialState } from './utils'
import { GetSettings } from '../use-persisted-channel-settings'
import { useAudioEngine } from '../../../components/core-audio-ui-unified/use-audio-engine'
import { useEventPaywall } from '../../event/use-event-paywall'
import { UseModalPaywall } from '../../event/use-modal-paywall'
import { ContextShell } from '../../../../../../packages/web-app-shell/src'

export interface UseEvent {
  onEventDispatch(i: { event: string; value: string }): void
}

interface UseEventPlayerProps {
  taskId: string
  isDemo?: boolean
  operationType?: string
  operations?: TaskOperations[]
  onGetChannelSettings: GetSettings
  onOpenModalPremium: UseModalPaywall['onOpen']
}

export const useEventPlayer = ({
  taskId,
  operations,
  operationType,
  isDemo = false,
  onGetChannelSettings,
  onOpenModalPremium
}: UseEventPlayerProps): UseEvent => {
  const router = useRouter()
  const {
    event: { sendEvent }
  } = useContext(ContextShell)
  const { dispatchEventPaywall } = useEventPaywall({
    onGetChannelSettings,
    onOpenModalPremium,
    isDemo
  })

  const [played, setPlayed] = useState(0)
  const [source, setSource] = useState('deepLink')
  const [mediaExported, setMediaExported] = useState(false)
  const [adaptApplied, setAdaptApplied] = useState('')
  const [updateApplied, setUpdateApplied] = useState(false)
  const [changeApplied, setChangeApplied] = useState(0)
  const [stemModified, { push: pushStemModified }] = useList<string>([])
  const [resetInteraction, { push: pushResetInteraction }] = useList<string>([])
  const [featureInteraction, { push: pushFeatureInteraction }] =
    useList<string>([])
  const [initialState] = useState(() =>
    getMixerInitialState(operations, onGetChannelSettings(true))
  )
  const playlistId = useMemo(
    () => router.query?.playlistId,
    [router.query?.playlistId]
  )

  const progressMs = useAudioEngine((p) => p.state.positionMs)

  const dispatchEventUrlClicked = useCallback(
    (name: string, url: string) => {
      sendEvent({
        name,
        category: 'engagement',
        customAttributes: {
          url,
          taskId,
          media_type: 'song',
          playlist_id: playlistId
        }
      })
    },
    [taskId, playlistId, sendEvent]
  )

  const dispatchEvent = useCallback(
    ({ detail: { event, value } }: any) => {
      if (event === 'played') {
        setPlayed(played + 1)
      } else if (event === 'media_exported') {
        setMediaExported(true)
      } else if (event === 'reset_interaction') {
        if (!resetInteraction.includes(value)) {
          pushResetInteraction(value)
        }
      } else if (event === 'feature_interaction') {
        if (!featureInteraction.includes(value)) {
          pushFeatureInteraction(value)
        }
      } else if (event === 'stem_modified') {
        const stem = stems.includes(value) ? value : undefined
        if (stem && !stemModified.includes(stem)) {
          pushStemModified(stem)
        }
      } else if (event === 'upgradability') {
        if (
          ['free-to-premium', 'premium-to-free'].includes(value) &&
          !adaptApplied
        ) {
          setAdaptApplied(value)
        }

        if (value === 'update') {
          setUpdateApplied(true)
        }

        if (value === 'change') {
          setChangeApplied(changeApplied + 1)
        }
      } else if (event === 'paywall') {
        dispatchEventPaywall()
      } else if (event === 'branded_media_url_clicked') {
        dispatchEventUrlClicked(event, value)
      }
    },
    [
      played,
      adaptApplied,
      stemModified,
      changeApplied,
      resetInteraction,
      featureInteraction,
      dispatchEventPaywall,
      pushResetInteraction,
      pushFeatureInteraction,
      dispatchEventUrlClicked,
      pushStemModified
    ]
  )

  useEffect(() => {
    /**
     * @deprecated
     * use method onEventDispatch instead of trigger event
     */
    on('player:event-dispatch', dispatchEvent)

    return () => {
      off('player:event-dispatch', dispatchEvent)
    }
  }, [dispatchEvent])

  const onEventDispatch = useCallback(
    ({ event, value }: { event: string; value: string }) => {
      dispatchEvent({ detail: { event, value } })
    },
    [dispatchEvent]
  )

  useEffectOnce(() => {
    if (router.query?.reference) {
      if (
        ['library', 'playlist_page'].includes(router.query?.reference as string)
      ) {
        setSource(router.query?.reference as string)
      }
      delete router.query.reference
      router.replace(router, undefined, { shallow: true })
    }
  })

  useUnmount(() => {
    sendEvent({
      name: 'media_interacted',
      category: 'engagement',
      customAttributes: {
        taskId,
        source,
        source_playlist: playlistId,
        is_demo: isDemo,
        play_clicked: played,
        play_time: Math.round((progressMs || 0) / 1000),
        feature_interaction: featureInteraction.join(','),
        reset_interaction: resetInteraction.join(','),
        initial_mixer_state: initialState.join(','),
        stem_modified: stemModified.join(','),
        media_exported: mediaExported,
        timestamp: new Date().toISOString(),
        operation_type: operationType,
        adapt_applied: adaptApplied,
        update_applied: updateApplied,
        change_applied: changeApplied
      }
    })
  })

  return {
    onEventDispatch
  }
}
